import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { Button, Popup, SelectBox, TextBox } from "devextreme-react";
import { LoadingPanel } from "../components/LoadingPanel";
import { useNavigate } from "react-router-dom";
import { ROUTE_POST_EXHIBITS_LIST_PAGE, ROUTE_CREATE_EDIT_POST_EXHIBIT, ROUTE_WELCOME } from "../routes/Routes";
import usePageTitle from "../hooks/usePageTitle";
import "./css/PostExhibits.scss";
import DataGridPostExhibits from "./components/DataGridPostExhibits";
import { fetchPostExhibits, fetchUserSettings, updateUserSettings } from "./PostExhibitsService";
import IsNullOrEmpty from "../components/IsNullOrEmpty";
import { getPostExhibitStatusIdByName } from "../Enums/postExhibitStatuses";
import notify from "devextreme/ui/notify";

const MAX_QUERIES = 10;
const SETTING_NAME = 'PostExhibitQueries';

export default function PostExhibitsList() {
    const [loading, setLoading] = useState(false);
    const [postExhibits, setPostExhibits] = useState(null);
    const [error, setError] = useState();
    const navigate = useNavigate();
    const { title } = ROUTE_POST_EXHIBITS_LIST_PAGE;
    const navigateToHomePage = () => navigate(ROUTE_WELCOME.withSlash);

    const gridRef = useRef(null);
    const { user } = useAuth();
    const userId = user.email;
    const [savedQueries, setSavedQueries] = useState([]);
    const [userSettings, setUserSettings] = useState(null);
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [popupVisible, setPopupVisible] = useState(false);
    const [queryName, setQueryName] = useState('');
    const [saving, setSaving] = useState(false);
    usePageTitle(`${title} - DHS FPS RMS`);

    useEffect(() => {
        loadPostExhibits();
    }, []);

    const onBackClick = () => {
        navigateToHomePage();
    };

    const loadPostExhibits = async () => {
        setLoading(true);
        try {
            const draftStatusId = getPostExhibitStatusIdByName('Draft');
            const data = await fetchPostExhibits();
            setPostExhibits(
                data.filter(p =>
                    p.PostExhibitStatus?.PostExhibitStatusId !== draftStatusId ||
                    IsNullOrEmpty(p.ClonedPostExhibitId)
                )
            );
        } catch (error) {
            setError(error.message || "Error loading Post Exhibits");
        }
        setLoading(false);
    }

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const setting = await fetchUserSettings(userId, SETTING_NAME);
                setUserSettings(setting);
                if (setting && setting.SettingValue) {
                    setSavedQueries(JSON.parse(setting.SettingValue));
                }
            } catch (e) {
                // Handle error or show notification
            }
        };
        loadSettings();
    }, [userId]);

    const handleLoadQuery = (state) => {
        const grid = gridRef.current.instance();
        grid.state(state);
    };

    const handleOpenPopup = () => {
        if (selectedQuery) {
            setQueryName(selectedQuery);
        } else {
            setQueryName('');
        }
        setPopupVisible(true);
    };

    const handleSaveOrUpdateQuery = async () => {
        if (!queryName.trim()) {
            notify('Query name cannot be empty.', 'error', 2000);
            return;
        }
        // Prevent duplicate names (except for the one being updated)
        if (
            savedQueries.some(q =>
                q.name === queryName &&
                (!selectedQuery || (selectedQuery && q.name !== selectedQuery))
            )
        ) {
            notify('A query with this name already exists.', 'error', 2000);
            return;
        }
        setSaving(true);
        try {
            const grid = gridRef.current.instance();
            const state = await grid.state();
            let updatedQueries;
            let notificationMsg;
            if (selectedQuery) {
                // Override
                updatedQueries = savedQueries.map(q =>
                    q.name === selectedQuery
                        ? { name: queryName, state }
                        : q
                );
                notificationMsg = 'Query updated successfully.';
            } else {
                // Add new
                if (savedQueries.length >= MAX_QUERIES) {
                    notify('Maximum of 10 saved queries reached.', 'error', 2000);
                    setSaving(false);
                    return;
                }
                updatedQueries = [...savedQueries, { name: queryName, state }];
                notificationMsg = 'Query saved successfully.';
            }
            setSavedQueries(updatedQueries);
            setSelectedQuery(queryName);
            await saveQueriesToBackend(updatedQueries);
            setPopupVisible(false);
            notify(notificationMsg, 'success', 2000);
        } catch (err) {
            notify('Failed to save query: ' + err.message, 'error', 3000);
        } finally {
            setSaving(false);
        }
    };

    const saveQueriesToBackend = async (queries) => {
        const now = new Date().toISOString().slice(0, 10);
        const payload = {
            Id: userSettings?.Id ?? 0,
            UserId: userSettings?.UserId ?? 0,
            UserEmail: userId,
            SettingName: SETTING_NAME,
            SettingValue: JSON.stringify(queries),
            CreatedDate: userSettings?.CreatedDate ?? now,
            CreatedBy: userSettings?.CreatedBy ?? userId,
            UpdatedDate: now,
            UpdatedBy: userId,
        };
        await updateUserSettings(payload);
    };

    return (
        <>
            <h1>{title}</h1>
            {error && <p>"Something went wrong.</p>}
            {loading && !postExhibits && <LoadingPanel location={{ of: '#postExhibit-list' }} message="Loading Post Exhibits List..." />}
            {postExhibits &&
                <div id='postExhibit-list'>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 8
                    }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button
                                icon='plus'
                                stylingMode="contained"
                                text="Add Post Exhibit"
                                onClick={() => navigate(ROUTE_CREATE_EDIT_POST_EXHIBIT.withSlash)}
                                id="add_new_postexhibit_button_029"
                            />
                            <Button
                                text='Home'
                                icon='home'
                                stylingMode='outlined'
                                type='normal'
                                onClick={onBackClick}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Button
                                text={selectedQuery ? "Update Query" : "Save Query"}
                                icon="save"
                                stylingMode="outlined"
                                onClick={handleOpenPopup}
                            />
                            <SelectBox
                                dataSource={savedQueries}
                                displayExpr="name"
                                valueExpr="name"
                                value={selectedQuery}
                                placeholder="Select Query"
                                style={{ minWidth: 180 }}
                                onValueChanged={e => {
                                    setSelectedQuery(e.value);
                                    const grid = gridRef.current.instance();
                                    if (!e.value) {
                                        grid.state({});
                                    } else {
                                        const match = savedQueries.find(q => q.name === e.value);
                                        if (match) handleLoadQuery(match.state);
                                    }
                                }}
                                showClearButton={true}
                                onOpened={() => {
                                    if (savedQueries.length === 0) {
                                        notify('No saved queries found.', 'info', 2000);
                                    }
                                }}
                            />
                        </div>
                    </div>

                    <Popup
                        visible={popupVisible}
                        onHiding={() => setPopupVisible(false)}
                        showTitle={true}
                        title={selectedQuery ? "Update Query" : "Save Query"}
                        width={350}
                        height={selectedQuery ? 220 : 180}
                    >
                        <div style={{ padding: 16 }}>
                            {selectedQuery && (
                                <div style={{ marginBottom: 8 }}>
                                    <b>Current Name:</b> {selectedQuery}
                                </div>
                            )}
                            <TextBox
                                value={queryName}
                                onValueChanged={e => setQueryName(e.value)}
                                placeholder="Query Name"
                                style={{ width: '100%' }}
                            />
                            <div style={{ marginTop: 16, textAlign: 'right' }}>
                                <Button
                                    text={selectedQuery ? "Update" : "Save"}
                                    type="default"
                                    disabled={saving}
                                    onClick={handleSaveOrUpdateQuery}
                                />
                                <Button
                                    text="Cancel"
                                    stylingMode="text"
                                    onClick={() => setPopupVisible(false)}
                                    style={{ marginLeft: 8 }}
                                />
                            </div>
                        </div>
                    </Popup>

                    <DataGridPostExhibits
                        dataSource={postExhibits}
                        isCostEstimate={false}
                        gridRef={gridRef}
                    />
                </div>
            }
        </>
    )
}